import React, { useCallback, useEffect, useMemo, useState } from "react";

import useControlPanel from "src/hooks/useControlPanel";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import Pagination from "src/components/Pagination/Pagination";
import ReleaseGroupControlPanel from "../../components/ControlPanel/ReleaseGroupControlPanel";
import ReleaseGroupTable from "../../components/Table/ReleaseGroupTable";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import { getReleaseGroup } from "src/apis/VersionRelease/group.api";
import { path } from "src/constants/path";
import { dataStateType } from "src/constants/status";

const initReleaseGroupData = {
  list: [],
  total_record: 0,
};

const titlePage = "Release Group";

const ReleaseGroup = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  // cache
  const { currentActionCache } = useCurrentActionCache();

  const [releaseGroupData, setReleaseGroupData] =
    useState(initReleaseGroupData);

  const [filterReleaseGroups, setFilterReleaseGroups] = useState([]);
  const permissionsAccessControl = useGetPermissionAccessControl();

  const fetchReleaseGroups = useCallback(() => {
    getReleaseGroup({}, (result) => {
      setReleaseGroupData({ ...result });
      setFilterReleaseGroups([...result.list]);
    });
  }, []);

  useEffect(() => {
    fetchReleaseGroups();
  }, [fetchReleaseGroups]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredReleaseGroup = useCallback((releaseGroups) => {
    setFilterReleaseGroups(releaseGroups);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (releaseGroups, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return releaseGroups.filter((releaseGroup) => {
        return releaseGroup.name
          .toLowerCase()
          .includes(key.toLowerCase().trim());
      });
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredReleaseGroups = value
        ? searchByKeyWord(releaseGroupData.list, value)
        : releaseGroupData.list;
      updateFilteredReleaseGroup(filteredReleaseGroups);
    },
    [releaseGroupData.list, searchByKeyWord, updateFilteredReleaseGroup]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterReleaseGroups, searchKey),
    [filterReleaseGroups, searchByKeyWord, searchKey]
  );
  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>

      <ReleaseGroupControlPanel
        currentActionCache={currentActionCache}
        permissionsAccessControl={permissionsAccessControl}
        page={page}
        limit={limit}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={true}
        />
      </ReleaseGroupControlPanel>

      <ReleaseGroupTable
        limit={limit}
        page={page}
        data={listItem}
        status={status}
      />

      <Pagination
        pathname={path.releaseGroup}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default ReleaseGroup;
